import React, { useEffect, useState } from 'react';

import { showToast } from '~/components/Shared/EcToast/showToast';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { api } from '~/redux/api/createApi';
import { ERROR, SUCCESS } from '~/types/toast.types';
import Divider from '~/ui/components/Divider/Divider';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Link from '~/ui/components/Link/Link';
import LoadingContainer from '~/ui/components/LoadingContainer/LoadingContainer';
import Text from '~/ui/components/Text/Text';
import { copyToClipboard } from '~/utils/helper.utils';

const messages = defineMessages({
  step1header: {
    id: 'AmazonS3Instruction.step1.header',
    defaultMessage: `Step 1) How to create the bucket access policy`,
    description: 'Header for the first step in the Amazon S3 instructions',
  },
  step1line1: {
    id: 'AmazonS3Instruction.step1.line1',
    defaultMessage: `1. Sign in to the AWS Management Console as an administrator of the Production account, and open the IAM console.`,
    description: 'Instruction for the first step in the Amazon S3 instructions',
  },
  step1line2: {
    id: 'AmazonS3Instruction.step1.line2',
    defaultMessage: `2. In the navigation pane on the left, choose Policies and then choose Create policy.`,
    description:
      'Instruction for the second step in the Amazon S3 instructions',
  },
  step1line3: {
    id: 'AmazonS3Instruction.step1.line3',
    defaultMessage: `3. Choose the JSON tab and copy the text from the following JSON policy document. Paste this text into the JSON text box, replacing "EVISORT_SHARED_BUCKET_NAME" with the name of the bucket you will be sharing with Evisort.`,
    description: 'Instruction for the third step in the Amazon S3 instructions',
  },
  step1line4: {
    id: 'AmazonS3Instruction.step1.line4',
    defaultMessage: `4. Resolve any security warnings, errors, or general warnings generated during policy validation, and then choose Review policy.`,
    description:
      'Instruction for the fourth step in the Amazon S3 instructions',
  },
  step1line5: {
    id: 'AmazonS3Instruction.step1.line5',
    defaultMessage: `5. On the Review page, type "read-write-evisort-bucket" for the policy name. Review the policy Summary to see the permissions granted by your policy, and then choose Create policy to save your work.`,
    description: 'Instruction for the fifth step in the Amazon S3 instructions',
  },
  step2header: {
    id: 'AmazonS3Instruction.step2.header',
    defaultMessage: `Step 2) How to create the bucket access role`,
    description: 'Header for the second step in the Amazon S3 instructions',
  },
  step2line1: {
    id: 'AmazonS3Instruction.step2.line1',
    defaultMessage: `1. In the navigation pane of the IAM console, choose Roles and then choose Create role.`,
    description:
      'Instruction for the first step in the second part of the Amazon S3 instructions',
  },
  step2line2: {
    id: 'AmazonS3Instruction.step2.line2',
    defaultMessage: `2. Choose the Another AWS account role type.`,
    description:
      'Instruction for the second step in the second part of the Amazon S3 instructions',
  },
  step2line3: {
    id: 'AmazonS3Instruction.step2.line3',
    defaultMessage: `3. For Account ID, type "216611480031".  Your External ID is "{externalId}", and type the ID provided to you along with this document.`,
    description:
      'Instruction for the third step in the second part of the Amazon S3 instructions. Example: externalId = "ext-abc123"',
  },
  step2line4: {
    id: 'AmazonS3Instruction.step2.line4',
    defaultMessage: `4. Choose Next: Permissions to set the permissions that will be associated with the role.`,
    description:
      'Instruction for the fourth step in the second part of the Amazon S3 instructions',
  },
  step2line5: {
    id: 'AmazonS3Instruction.step2.line5',
    defaultMessage: `5. Select the box next to the "read-write-evisort-bucket" policy that you created previously, then choose Next: Tags.`,
    description:
      'Instruction for the fifth step in the second part of the Amazon S3 instructions',
  },
  step2line6: {
    id: 'AmazonS3Instruction.step2.line6',
    defaultMessage: `6. Choose Next: Review and type "EvisortSync" for the role name.  You must use this name, as our internal permissions system is checking for it.`,
    description:
      'Instruction for the sixth step in the second part of the Amazon S3 instructions',
  },
  step2line7: {
    id: 'AmazonS3Instruction.step2.line7',
    defaultMessage: `7. After reviewing the role, choose "Create" role.`,
    description:
      'Instruction for the seventh step in the second part of the Amazon S3 instructions',
  },
  step3header: {
    id: 'AmazonS3Instruction.step3.header',
    defaultMessage: `Step 3) How to obtain the ARN of the bucket access role`,
    description: 'Header for the third step in the Amazon S3 instructions',
  },
  step3line1: {
    id: 'AmazonS3Instruction.step3.line1',
    defaultMessage: `1. In the navigation pane of the IAM console, choose Roles.`,
    description:
      'Instruction for the first step in the third part of the Amazon S3 instructions',
  },
  step3line2: {
    id: 'AmazonS3Instruction.step3.line2',
    defaultMessage: `2. In the list of roles, choose the "EvisortSync" role.`,
    description:
      'Instruction for the second step in the third part of the Amazon S3 instructions',
  },
  step3line3: {
    id: 'AmazonS3Instruction.step3.line3',
    defaultMessage: `3. In the Summary section of the details pane, copy the Role ARN value.`,
    description:
      'Instruction for the third step in the third part of the Amazon S3 instructions',
  },
  step3line4: {
    id: 'AmazonS3Instruction.step3.line4',
    defaultMessage: `4. Provide the Role ARN value to the Evisort Admin, who will use it when setting up the Sync Pair.`,
    description:
      'Instruction for the fourth step in the third part of the Amazon S3 instructions',
  },
});
const AWS_POLICY_CODE_BLOCK = [
  '{',
  '  "Version": "2012-10-1<7",',
  '  "Statement": [',
  '    {',
  '      "Effect": "Allow",',
  '      "Action": "s3:ListAllMyBuckets",',
  '      "Resource": "*"',
  '    },',
  '    {',
  '      "Effect": "Allow",',
  '      "Action": [',
  '        "s3:ListBucket",',
  '        "s3:GetBucketLocation"',
  '      ],',
  '      "Resource": "arn:aws:s3:::EVISORT_SHARED_BUCKET_NAME"',
  '    },',
  '    {',
  '      "Effect": "Allow"',
  '      "Action": [',
  '        "s3:GetObject",',
  '        "s3:PutObject",',
  '        "s3:DeleteObject"',
  '      ],',
  '      "Resource": "arn:aws:s3:::EVISORT_SHARED_BUCKET_NAME"',
  '    }',
  '  ]',
  '}',
].join('\n');

const buildInstructions = (externalId, intl) =>
  [
    // Step 1
    intl.formatMessage(messages.step1header),
    intl.formatMessage(messages.step1line1),
    intl.formatMessage(messages.step1line2),
    intl.formatMessage(messages.step1line3),
    '',
    AWS_POLICY_CODE_BLOCK,
    '',
    intl.formatMessage(messages.step1line4),
    intl.formatMessage(messages.step1line5),
    '',
    // Step 2
    intl.formatMessage(messages.step2header),
    intl.formatMessage(messages.step2line1),
    intl.formatMessage(messages.step2line2),
    intl.formatMessage(messages.step2line3, { externalId }),
    intl.formatMessage(messages.step2line4),
    intl.formatMessage(messages.step2line5),
    intl.formatMessage(messages.step2line6),
    intl.formatMessage(messages.step2line7),
    '',
    // Step 3
    intl.formatMessage(messages.step3header),
    intl.formatMessage(messages.step3line1),
    intl.formatMessage(messages.step3line2),
    intl.formatMessage(messages.step3line3),
    intl.formatMessage(messages.step3line4),
  ].join('\n');

function AmazonS3Instruction() {
  const AWS_MANAGEMENT_CONSOLE = 'https://aws.amazon.com/console/';
  const intl = useIntl();

  const [doesShowInstructions, setDoesShowInstructions] = useState(true);

  const currentUser = useCurrentUser();

  const { data, error, isFetching } = api.endpoints.getExternalId.useQuery({
    clientId: currentUser.client,
  });

  const externalId = data?.externalId ?? '';

  useEffect(() => {
    if (error) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'AmazonS3Instruction.fetchExternalIdError',
          defaultMessage: 'Failed to fetch external ID. Please try again.',
          description:
            'Error message when fetching external ID for Amazon S3 instructions',
        }),
      );
    }
  }, [error]);

  const redirectToAWSManagementConsole = () => {
    window.open(AWS_MANAGEMENT_CONSOLE, '_blank');
  };

  return (
    <FlexLayout flexDirection="column" sx={{ width: '100%' }}>
      <FlexLayout pb={3}>
        <FormattedMessage
          id="AmazonS3Instruction.loginToAWSManagementConsole"
          defaultMessage="Login your<link> AWS Management Console</link>."
          description="Message prompting user to login to AWS Management Console"
          values={{
            link: (chunks) => (
              <Link onClick={() => redirectToAWSManagementConsole()}>
                {chunks}
              </Link>
            ),
          }}
        />
      </FlexLayout>

      <FlexLayout
        flexDirection="column"
        sx={{ border: '#e5e5e5 solid 1px', borderRadius: '3px' }}
      >
        <FlexLayout
          alignItems="center"
          justifyContent="space-between"
          p={3}
          sx={{ width: '100%' }}
        >
          <FlexLayout alignItems="center" space={1}>
            <IconButton
              icon={doesShowInstructions ? 'chevron-up' : 'chevron-down'}
              onClick={() => setDoesShowInstructions(!doesShowInstructions)}
            />
            <Text>
              {intl.formatMessage({
                id: 'AmazonS3Instruction.bucketAccessInstructionsHeader',
                defaultMessage:
                  'Instructions for AWS Admin to get access to source bucket',
                description: 'Header for Amazon S3 instructions',
              })}
            </Text>
          </FlexLayout>
          <IconButton
            icon="clipboard"
            onClick={() =>
              copyToClipboard(
                buildInstructions(externalId, intl),
                intl.formatMessage({
                  id: 'AmazonS3Instruction.copyToClipboardSuccess',
                  defaultMessage: 'Data copied to clipboard.',
                  description: 'Success message for copying data to clipboard',
                }),

                SUCCESS,
              )
            }
            tooltip={intl.formatMessage({
              id: 'AmazonS3Instruction.copyToClipboardTooltip',
              defaultMessage: 'Copy to clipboard',
              description: 'Tooltip for copy to clipboard button',
            })}
          />
        </FlexLayout>

        <Divider />

        <LoadingContainer isLoading={isFetching}>
          {doesShowInstructions && !isFetching && (
            <>
              <FlexLayout pl={3} pt={5}>
                <Text variant="subtitle">
                  {intl.formatMessage(messages.step1header)}
                </Text>
              </FlexLayout>
              <FlexLayout flexDirection="column" space={3} mx={5} p={5} as="ol">
                <Text as="li" variant="xs-dense">
                  {intl.formatMessage({
                    id: 'AmazonS3Instruction.signInToAWSConsole',
                    defaultMessage:
                      'Sign in to the AWS Management Console as an administrator of the Production account, and open the IAM console.',
                    description: 'Instruction to sign in to AWS console',
                  })}
                </Text>
                <Text as="li" variant="xs-dense">
                  {intl.formatMessage({
                    id: 'AmazonS3Instruction.choosePolicies',
                    defaultMessage:
                      'In the navigation pane on the left, choose Policies and then choose Create policy.',
                    description:
                      'Instruction to choose policies in AWS console',
                  })}
                </Text>
                <Text as="li" variant="xs-dense">
                  <FormattedMessage
                    id="AmazonS3Instruction.chooseJSONTab"
                    defaultMessage='Choose the JSON tab and copy the text from the following JSON policy document. Paste this text into the JSON text box, replacing <strong>"EVISORT_SHARED_BUCKET_NAME"</strong> with the name of the bucket you will be sharing with Evisort.'
                    description="Instruction to choose JSON tab in AWS console"
                    values={{
                      strong: (chunks) => <strong>{chunks}</strong>,
                    }}
                  />
                </Text>

                <FlexLayout bg="#f7f6f3" p={5}>
                  <Text variant="2xs-dense" color="gray-600">
                    &#123;
                    <br />
                    <Text ml={4} variant="2xs-dense" color="#8c1c54">
                      "Version"
                    </Text>
                    :&nbsp;
                    <Text variant="2xs-dense" color="#71972b">
                      "2012-10-17"
                    </Text>
                    ,<br />
                    <Text ml={4} variant="2xs-dense" color="#8c1c54">
                      "Statement"
                    </Text>
                    : [<br />
                    <Text ml={6} color="#9f9f9f">
                      &#123;
                    </Text>
                    <br />
                    <Text ml={10} variant="2xs-dense" color="#8c1c54">
                      "Effect"
                    </Text>
                    :&nbsp;
                    <Text variant="2xs-dense" color="#71972b">
                      "Allow"
                    </Text>
                    ,<br />
                    <Text ml={10} variant="2xs-dense" color="#8c1c54">
                      "Action"
                    </Text>
                    :&nbsp;
                    <Text variant="2xs-dense" color="#71972b">
                      "s3:ListAllMyBuckets"
                    </Text>
                    ,<br />
                    <Text ml={10} variant="2xs-dense" color="#8c1c54">
                      "Resource"
                    </Text>
                    : <Text color="#71972b">"*"</Text>
                    <br />
                    <Text ml={6} variant="2xs-dense" color="#9f9f9f">
                      &#125;
                    </Text>
                    ,<br />
                    <Text ml={6} variant="2xs-dense" color="#9f9f9f">
                      &#123;
                    </Text>
                    <br />
                    <Text ml={10} variant="2xs-dense" color="#8c1c54">
                      "Effect"
                    </Text>
                    :&nbsp;
                    <Text variant="2xs-dense" color="#71972b">
                      "Allow"
                    </Text>
                    ,<br />
                    <Text ml={10} variant="2xs-dense" color="#8c1c54">
                      "Action"
                    </Text>
                    : [<br />
                    <Text ml={12} variant="2xs-dense" color="#71972b">
                      "s3:ListBucket"
                    </Text>
                    ,<br />
                    <Text ml={12} variant="2xs-dense" color="#71972b">
                      "s3:GetBucketLocation"
                    </Text>
                    <br />
                    <Text ml={10} variant="2xs-dense" color="#gray-600">
                      ]
                    </Text>
                    ,<br />
                    <Text ml={10} variant="2xs-dense" color="#8c1c54">
                      "Resource"
                    </Text>
                    :
                    <Text variant="2xs-dense" color="#71972b">
                      &nbsp; "arn:aws:s3:::EVISORT_SHARED_BUCKET_NAME"
                    </Text>
                    <br />
                    <Text ml={6} variant="2xs-dense" color="#9f9f9f">
                      &#125;
                    </Text>
                    ,<br />
                    <Text ml={6} variant="2xs-dense" color="#9f9f9f">
                      &#123;
                    </Text>
                    <br />
                    <Text ml={8} variant="2xs-dense" color="#8c1c54">
                      "Effect"
                    </Text>
                    :&nbsp;
                    <Text variant="2xs-dense" color="#71972b">
                      "Allow"
                    </Text>
                    ,<br />
                    <Text ml={8} variant="2xs-dense" color="#8c1c54">
                      "Action"
                    </Text>
                    : [<br />
                    <Text ml={12} variant="2xs-dense" color="#71972b">
                      "s3:GetObject"
                    </Text>
                    ,<br />
                    <Text ml={12} variant="2xs-dense" color="#71972b">
                      "s3:PutObject"
                    </Text>
                    ,<br />
                    <Text ml={12} variant="2xs-dense" color="#71972b">
                      "s3:DeleteObject"
                    </Text>
                    <br />
                    <Text ml={10} variant="2xs-dense" color="#gray-600">
                      ]
                    </Text>
                    ,<br />
                    <Text ml={10} variant="2xs-dense" color="#8c1c54">
                      "Resource"
                    </Text>
                    :&nbsp;
                    <Text variant="2xs-dense" color="#71972b">
                      "arn:aws:s3:::EVISORT_SHARED_BUCKET_NAME/*"
                    </Text>
                    <br />
                    <Text ml={6} variant="2xs-dense" color="gray-600">
                      &#125;
                    </Text>
                    <br />
                    <Text ml={4} variant="2xs-dense" color="gray-600">
                      ]
                    </Text>
                    <br />
                    &#125;
                  </Text>
                </FlexLayout>

                <Text as="li" variant="xs-dense">
                  {intl.formatMessage({
                    id: 'AmazonS3Instruction.resolveWarnings',
                    defaultMessage:
                      'Resolve any security warnings, errors, or general warnings generated during policy validation, and then choose Review policy.',
                    description:
                      'Instruction to resolve warnings in AWS console',
                  })}
                </Text>
                <Text as="li" variant="xs-dense">
                  <FormattedMessage
                    id="AmazonS3Instruction.reviewPolicy"
                    defaultMessage='On the Review page, type "<strong>read-write-evisort-bucket</strong>" for the policy name. Review the policy Summary to see the permissions granted by your policy, and then choose Create policy to save your work.'
                    description="Instruction to review policy in AWS console"
                    values={{
                      strong: (chunks) => <strong>{chunks}</strong>,
                    }}
                  />
                </Text>
              </FlexLayout>

              <Divider />

              <FlexLayout pl={3} pt={5}>
                <Text variant="subtitle">
                  {intl.formatMessage(messages.step2header)}
                </Text>
              </FlexLayout>
              <FlexLayout flexDirection="column" space={3} mx={5} p={5} as="ol">
                <Text as="li" variant="xs-dense">
                  {intl.formatMessage({
                    id: 'AmazonS3Instruction.instructionStep2',
                    defaultMessage:
                      'In the navigation pane of the IAM console, choose Roles and\n                  then choose Create role.',
                    description: 'Instruction for creating role in AWS console',
                  })}
                </Text>
                <Text as="li" variant="xs-dense">
                  {intl.formatMessage({
                    id: 'AmazonS3Instruction.chooseAnotherAWSAccount',
                    defaultMessage: 'Choose the Another AWS account role type.',
                    description:
                      'Instruction to choose another AWS account role type',
                  })}
                </Text>

                <Text as="li" variant="xs-dense">
                  <FormattedMessage
                    id="AmazonS3Instruction.accountIdAndExternalId"
                    defaultMessage='For <strong>Account ID</strong>, type "<strong>216611480031</strong>". Your <strong>External ID</strong> is "<strong>{externalId}</strong>", and type the ID provided to you along with this document.'
                    description="Instruction for account ID and external ID in AWS console. Example: externalId = 'EXT123456'"
                    values={{
                      strong: (chunks) => <strong>{chunks}</strong>,
                      externalId,
                    }}
                  />
                </Text>

                <Text as="li" variant="xs-dense">
                  {intl.formatMessage({
                    id: 'AmazonS3Instruction.nextPermissions',
                    defaultMessage:
                      'Choose Next: Permissions to set the permissions that will be associated with the role.',
                    description:
                      'Instruction to set permissions in AWS console',
                  })}
                </Text>
                <Text as="li" variant="xs-dense">
                  <FormattedMessage
                    id="AmazonS3Instruction.selectPolicy"
                    defaultMessage='Select the box next to the "<strong>read-write-evisort-bucket</strong>" policy that you created previously, then choose Next: Tags.'
                    description="Instruction to select policy in AWS console"
                    values={{
                      strong: (chunks) => <strong>{chunks}</strong>,
                    }}
                  />
                </Text>
                <Text as="li" variant="xs-dense">
                  <FormattedMessage
                    id="AmazonS3Instruction.nextReview"
                    defaultMessage='Choose Next: Review and type "<strong>EvisortSync</strong>" for the role name. You must use this name, as our internal permissions system is checking for it.'
                    description="Instruction to review role in AWS console"
                    values={{
                      strong: (chunks) => <strong>{chunks}</strong>,
                    }}
                  />
                </Text>
                <Text as="li" variant="xs-dense">
                  <FormattedMessage
                    id="AmazonS3Instruction.createRole"
                    defaultMessage='After reviewing the role, choose "<strong>Create</strong>" role.'
                    description="Instruction to create role in AWS console"
                    values={{
                      strong: (chunks) => <strong>{chunks}</strong>,
                    }}
                  />
                </Text>
              </FlexLayout>

              <Divider />

              <FlexLayout pl={3} pt={5}>
                <Text variant="subtitle">
                  {intl.formatMessage(messages.step3header)}
                </Text>
              </FlexLayout>
              <FlexLayout flexDirection="column" space={3} mx={5} p={5} as="ol">
                <Text as="li" variant="xs-dense">
                  {intl.formatMessage({
                    id: 'AmazonS3Instruction.choseRoles',
                    defaultMessage:
                      'In the navigation pane of the IAM console, choose Roles.',
                    description: 'Instruction to choose roles in AWS console',
                  })}
                </Text>
                <Text as="li" variant="xs-dense">
                  <FormattedMessage
                    id="AmazonS3Instruction.chooseEvisortSyncRole"
                    defaultMessage='In the list of roles, choose the "<strong>EvisortSync</strong>" role.'
                    description="Instruction to choose EvisortSync role in AWS console"
                    values={{
                      strong: (chunks) => <strong>{chunks}</strong>,
                    }}
                  />
                </Text>
                <Text as="li" variant="xs-dense">
                  {intl.formatMessage({
                    id: 'AmazonS3Instruction.copyRoleArn',
                    defaultMessage:
                      'In the Summary section of the details pane, copy the Role ARN value.',
                    description: 'Instruction to copy Role ARN in AWS console',
                  })}
                </Text>
                <Text as="li" variant="xs-dense">
                  {intl.formatMessage({
                    id: 'AmazonS3Instruction.provideRoleArnToEvisortAdmin',
                    defaultMessage:
                      'Provide the Role ARN value to the Evisort Admin, who will use it when setting up the Sync Pair.',
                    description:
                      'Instruction to provide Role ARN to Evisort Admin',
                  })}
                </Text>
              </FlexLayout>
            </>
          )}
        </LoadingContainer>
      </FlexLayout>
    </FlexLayout>
  );
}

export default AmazonS3Instruction;
